import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Eye, User, Share2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DiamondLogo } from '../components/shared/DiamondLogo';
import PromoBanner from '../components/shared/PromoBanner';
import { renderMarkdown } from '../lib/markdown';
import { capturePromoFromUrl, withPromo } from '../lib/promoLink';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const setMeta = (name, content, isProperty = false) => {
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Capture ?promo=CODE from the article URL so the CTA inherits it
    capturePromoFromUrl();
    setLoading(true);
    setNotFound(false);
    fetch(`${API}/api/articles/${slug}`)
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        setArticle(data);
        // SEO meta tags for link unfurls (iMessage / Slack / Twitter / FB)
        const title = `${data.title} · Diamond Scorer`;
        const desc = (data.excerpt || `Read "${data.title}" on the Diamond Scorer blog.`).slice(0, 300);
        document.title = title;
        setMeta('description', desc);
        setMeta('og:title', data.title, true);
        setMeta('og:description', desc, true);
        setMeta('og:type', 'article', true);
        if (data.cover_image_url) {
          setMeta('og:image', data.cover_image_url, true);
          setMeta('twitter:image', data.cover_image_url);
        }
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', data.title);
        setMeta('twitter:description', desc);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.excerpt || article.title, url });
      } catch {/* user canceled */}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      } catch {
        toast.error('Could not copy link');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500 gap-2">
        <Loader2 size={18} className="animate-spin" /> Loading article…
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-stone-50 p-4 flex items-center justify-center" data-testid="article-not-found">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-stone-800 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            ARTICLE NOT FOUND
          </h1>
          <p className="text-stone-600 text-sm mb-4">This story may have been moved or unpublished.</p>
          <Button onClick={() => navigate('/blog')} className="bg-green-700 hover:bg-green-800 text-white" data-testid="article-back-to-blog">
            Back to blog
          </Button>
        </div>
      </div>
    );
  }

  const html = renderMarkdown(article.body_markdown || '');

  return (
    <div className="min-h-screen bg-stone-50" data-testid="article-page">
      {/* Top nav strip */}
      <div className="bg-green-950 text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-sm font-semibold"
            data-testid="article-back-btn"
          >
            <ArrowLeft size={14} /> Back to blog
          </button>
          <a href="/" className="flex items-center gap-2 text-white hover:text-amber-200">
            <DiamondLogo size={22} />
            <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>DIAMOND SCORER</span>
          </a>
        </div>
      </div>

      {/* Cover */}
      {article.cover_image_url && (
        <div className="w-full bg-stone-200 aspect-[21/9] max-h-[440px] overflow-hidden">
          <img src={article.cover_image_url} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Article body */}
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <h1
          className="text-3xl md:text-5xl font-extrabold text-stone-900 mb-4 leading-tight"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid="article-title"
        >
          {article.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500 mb-8 pb-6 border-b border-stone-200">
          {article.author && (
            <span className="flex items-center gap-1"><User size={12} /> {article.author}</span>
          )}
          <span className="flex items-center gap-1">
            <Calendar size={12} /> {formatDate(article.created_at)}
          </span>
          {typeof article.view_count === 'number' && (
            <span className="flex items-center gap-1"><Eye size={12} /> {article.view_count} read{article.view_count === 1 ? '' : 's'}</span>
          )}
          <button
            onClick={share}
            className="ml-auto flex items-center gap-1 text-green-700 hover:text-green-900 font-semibold"
            data-testid="article-share-btn"
          >
            <Share2 size={12} /> Share
          </button>
        </div>

        {article.excerpt && (
          <p className="text-lg text-stone-700 italic mb-6 leading-relaxed" data-testid="article-excerpt">
            {article.excerpt}
          </p>
        )}

        <div
          className="prose-content"
          data-testid="article-body"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {/* Promo banner — visible if reader landed via /blog/:slug?promo=CODE */}
        <div className="mt-10">
          <PromoBanner />
        </div>

        {/* Tail CTA */}
        <div className="mt-6 bg-gradient-to-br from-green-900 to-green-700 rounded-2xl p-6 text-center text-white">
          <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            READY TO SCORE YOUR NEXT GAME?
          </h3>
          <p className="text-green-100 text-sm mb-4">
            Tap-to-score, AI lineups, and a live parent feed — all in Diamond Scorer.
          </p>
          <Button
            onClick={() => navigate(withPromo('/'))}
            className="bg-amber-400 hover:bg-amber-300 text-stone-900 font-bold"
            data-testid="article-cta-btn"
          >
            Try Diamond Scorer Free
          </Button>
        </div>
      </article>

      {/* Footer */}
      <div className="text-center pb-6 text-[11px] text-stone-500">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <a href="/" className="hover:text-stone-800">Home</a>
          <span>·</span>
          <a href="/blog" className="hover:text-stone-800">Blog</a>
          <span>·</span>
          <a href="/contact" className="hover:text-stone-800">Contact</a>
          <span>·</span>
          <a href="/privacy" className="hover:text-stone-800">Privacy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-stone-800">Terms</a>
        </div>
        <p className="text-[10px] text-stone-400 mt-2">Diamond Scorer &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
